/**
 * App.js
 *
 * Main application component that fetches GeoJSON map data, manages selected markers,
 * handles UI controls (inferred features toggle and product tour),
 * and displays both a Leaflet map and corresponding images with bounding boxes.
 */
import React, { useState, useCallback, useEffect, useMemo } from "react";
import "./App.css";
import MapComponent from "./components/Map/MapComponent";
import ImageWithBBoxes from "./components/ImageWithBBoxes/ImageWithBBoxes";
import { haversineDistance } from "./utils/helper";
import {
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Typography,
  Divider,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress
} from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TopBar from "./components/TopBar";
import ProductTourDialog from "./components/ProductTourDialog";

const DATA_URL = "https://provisodevstorage.blob.core.windows.net/road-viewer/roadside_objects.json"

function App() {
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showBoxes, setShowBoxes] = useState(true);
  const [currentMarkerIndex, setCurrentMarkerIndex] = useState(null);
  const [openTour, setOpenTour] = useState(() => {
    return localStorage.getItem("tourCompleted") !== "true";
  });
  const [relativePolygons, setRelativePolygons] = useState([]);
  const [geoJsonData, setGeoJsonData] = useState({ type: "FeatureCollection", features: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch GeoJSON data on component mount
   */
  useEffect(() => {
    fetch(DATA_URL)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(parsedData => {
        // Once the JSON data is successfully parsed, map over it to create GeoJSON features
        const features = parsedData.map((item, index) => ({
          type: "Feature",
          id: item.id || `feature-${index}`,
          geometry: {
            type: "Point",
            coordinates: [item.location.lon, item.location.lat],
          },
          // Include all other properties from the item in the feature's properties
          properties: {
            ...item,
          },
        }));
        setGeoJsonData({ type: "FeatureCollection", features });
        setLoading(false);
      })
      .catch(err => {
        setError(err);
        setLoading(false);
        console.error("Error fetching GeoJSON:", err);
      });
  }, []);

   /**
   * Format polygons from the selected feature for rendering
   */
  useEffect(() => {
    if (selectedFeature?.properties?.polygons && Array.isArray(selectedFeature.properties.polygons)) {
      const formattedPolygons = selectedFeature.properties.polygons.map(poly => ({
        label: poly.label,
        points: poly.polygon.map(coord => ({ x: coord[0], y: coord[1] })),
        color: poly.color || "#00FF00",
        strokeWidth: 2,
      }));
      setRelativePolygons(formattedPolygons);
    } else {
      setRelativePolygons([]);
    }
  }, [selectedFeature]);
  
  useEffect(() => {
    if (geoJsonData.features.length > 0 && selectedFeature === null) {
      const defaultLat = 48.28762;
      const defaultLon = -122.65778;
  
      let minDistance = Infinity;
      let closestFeature = null;
      let closestIndex = null;
  
      geoJsonData.features.forEach((feature, index) => {
        const [lon, lat] = feature.geometry.coordinates;
        const dist = haversineDistance(lat, lon, defaultLat, defaultLon);
        if (dist < minDistance) {
          minDistance = dist;
          closestFeature = feature;
          closestIndex = index;
        }
      });
  
      if (closestFeature) {
        setSelectedFeature(closestFeature);
        setCurrentMarkerIndex(closestIndex);
      }
    }
  }, [geoJsonData, selectedFeature]);
  

   /**
   * Derive unique features to prevent duplicate markers
   */
  const uniqueCoordinates = {};
  const uniqueFeatures = useMemo(() => {
    return geoJsonData.features.filter(feature => {
      const key = `${feature.geometry.coordinates[1]},${feature.geometry.coordinates[0]}`;
      // If the key hasn't been seen before, add it to the object and return true to keep the feature
      if (!uniqueCoordinates[key]) {
        uniqueCoordinates[key] = true;
        return true;
      }
      return false;
    });
  }, [geoJsonData.features]);

  const totalMarkers = uniqueFeatures.length;

  // useCallback hook for going to the previous marker
  const goToPreviousMarker = useCallback(() => {
    setCurrentMarkerIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : 0
    );
  }, []);

  // useCallback hook for going to the next marker
  const goToNextMarker = useCallback(() => {
    setCurrentMarkerIndex((prevIndex) =>
      prevIndex < totalMarkers - 1 ? prevIndex + 1 : totalMarkers - 1
    );
  }, [totalMarkers]);

  // useEffect hook to update the current marker index when the selectedFeature changes
  useEffect(() => {
    if (selectedFeature) {
      const index = uniqueFeatures.findIndex(
        (feature) => feature.properties.id === selectedFeature.properties.id
      );
      setCurrentMarkerIndex(index);
    }
  }, [selectedFeature, uniqueFeatures]);

  // Function to handle the "Don't show this tour again" checkbox
  const handleDontShowAgain = (dontShow) => {
    if (dontShow) {
      localStorage.setItem("tourCompleted", "true");
    }
  };
  // Function to handle closing the product tour dialog
  const handleCloseTour = (dontShowAgain) => {
    setOpenTour(false);
    if (dontShowAgain) {
      localStorage.setItem("tourCompleted", "true");
    }
  };

  // Construct the URL for the first image of the selected feature
  let firstImageUrl = "";
  if (selectedFeature) {
    try {
      firstImageUrl = `https://waylyticsstorage.blob.core.windows.net/sr-images/middle/${selectedFeature.properties.source_file.split('/').pop()}`;
    } catch (error) {
      console.error("Error loading image:", error);
      firstImageUrl = "";
    }
  }
  // Filter the objects array of the selected feature to only include those with a certain size
  const filteredObjects = selectedFeature?.properties.objects.filter(
    (obj) => obj.bbox.width >= 0.02 && obj.bbox.height >= 0.02
  ) || [];

  // Function to truncate labels that contain "Limit"
  const truncateAfterLimit = (label) => {
    const idx = label.indexOf("Limit");
    return idx >= 0 ? label.slice(0, idx + "Limit".length) : label;
  };
  
  const extractUniqueObjects = (data) => {
    const seen = new Set();
    const uniqueObjects = [];
    data.forEach(item => {
      const text = item.text || null;
      const key = `${item.label}_${item.color}_${text}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueObjects.push({
          label: item.label,
          color: item.color,
          text: text
        });
      }
    });
    
    return uniqueObjects;
  }

  if (loading) {
    return <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.8)', // Optional: light overlay
        zIndex: 1300,
        color: '#0f3176'
      }}
    >
      <CircularProgress />
    </Box>
      ;
  }

  if (error) {
    return <div>Error loading map data: {error.message}</div>;
  }

  return (
    <div className="App">
      <TopBar />
      <div className="main-container">
        <div className="right-side">
          <div className="map-container-wrapper">
            <MapComponent
              setSelectedFeature={setSelectedFeature}
              geoJsonData={geoJsonData}
              goToMarker={currentMarkerIndex}
              selectedFeatureIndex={currentMarkerIndex}
              showBoxes={showBoxes}
            />
          </div>
          <div className="info-container">
            {selectedFeature ? (
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                      Information
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showBoxes}
                          onChange={(e) => setShowBoxes(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Show Inferred Features "
                    />
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Location:</strong>{" "}
                    {
                      selectedFeature.geometry.coordinates
                        .slice()
                        .reverse()
                        .map(c => Number(c).toFixed(5))
                        .join(", ")
                    }
                  </Typography>
                  {selectedFeature.properties.heading && <Typography variant="body2" color="text.secondary">
                    <strong>Heading:</strong> {selectedFeature.properties.heading}
                  </Typography>}
                  {selectedFeature.properties.ocr?.parsed_object?.highway && (<Typography variant="body2" color="text.secondary">
                    <strong>Highway:</strong>{" "}
                    {selectedFeature.properties.ocr?.parsed_object?.highway}
                  </Typography>)}
                  {selectedFeature.properties.ocr?.parsed_object?.highway && (<Typography variant="body2" color="text.secondary">
                    <strong>Mile Post:</strong>{" "}
                    {selectedFeature.properties.ocr?.parsed_object?.mp}
                  </Typography>)}
                  {(selectedFeature?.properties?.polygons?.length > 0 || selectedFeature.properties.objects?.length > 0 && filteredObjects.length > 0) && showBoxes && (
                    <>
                      <Box sx={{ mt: 2, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Inferred Features:</strong>{" "}
                        </Typography>
                      </Box>
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                        <Table size="small" aria-label="legend table">
                          <TableHead>
                            <TableRow>
                              <TableCell>Label</TableCell>
                              <TableCell>Information</TableCell>
                              <TableCell>Color</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {extractUniqueObjects(filteredObjects).map((obj, i) => (
                              <TableRow key={i}>
                                <TableCell>{obj.label}</TableCell>
                                <TableCell>{obj.text ? `${obj.text}` : '-'}</TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      bgcolor: obj.color,
                                      border: "1px solid #666",
                                      borderRadius: "2px",
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                            {extractUniqueObjects(selectedFeature.properties.polygons).map((poly, i) => (
                              <TableRow key={i}>
                                <TableCell>{poly.label}</TableCell>
                                <TableCell>{poly.text ? `${poly.text}` : '-'}</TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      bgcolor: poly.color,
                                      border: "1px solid #666",
                                      borderRadius: "2px",
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}

                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Typography variant="subtitle1" color="text.secondary">
                Select a point to see details.
              </Typography>
            )}
          </div>
        </div>
        <div className="image-side">
          {selectedFeature ? (
            <Card className="image-card" elevation={3}>
              <CardContent className="image-content">
                <div className="image-container">
                  <ImageWithBBoxes
                    imageSrc={firstImageUrl}
                    boundingBoxes={showBoxes ? selectedFeature.properties.objects : []}
                    polygons={showBoxes ? relativePolygons : []}
                  />
                </div>
                {firstImageUrl && <Box className="image-buttons">
                  <Button
                    style={{ backgroundColor: '#000080' }}
                    variant="contained"
                    onClick={goToPreviousMarker}
                    disabled={currentMarkerIndex === 0}
                  >
                    <ChevronLeftIcon />
                    Previous
                  </Button>
                  <Button
                    style={{ backgroundColor: '#000080' }}
                    variant="contained"
                    onClick={goToNextMarker}
                    disabled={currentMarkerIndex === totalMarkers - 1 || totalMarkers === 0}
                  >
                    Next
                    <ChevronRightIcon />
                  </Button>
                </Box>}
              </CardContent>
            </Card>
          ) : (
            <Typography variant="subtitle1" color="text.secondary">
              Click on a marker to view its details.
            </Typography>
          )}
        </div>
        <ProductTourDialog open={openTour} onClose={handleCloseTour} onDontShowAgain={handleDontShowAgain} />
      </div>
    </div>
  );
}

export default App;